<template>
  <div class="imageWrapper">
    <div class="smallPreview">
      <v-icon v-if="displayFavoriteIcon"
              :class="{favoriteIcon: true, small: true, active: image.favorite, editable: favoriteEditable}"
              @click="evt => toggleFavorite(image, evt)">
        {{ getFavoriteIconName(image) }}
      </v-icon>
      <v-img :src="smallImageUrl" :height="getSmallHeight" :width="getSmallWidth"
             :class="{smallImage: true, landscape: landscape, portrait: !landscape}" @click="onImageClick" alt=""
             ref="small" @load="onSmallLoaded"/>
      <span class="label">{{ displayId }}</span>
    </div>
    <div class="largePreview">
      <v-dialog v-if="mayLoadLargeImage || dialog" content-class="largePreviewDialog" v-model="dialog"
                :max-width="largeImageWidth()">
        <v-icon v-if="displayFavoriteIcon && largeLoaded"
                class="favoriteIcon large"
                :class="{editable: favoriteEditable}"
                @click="evt => toggleFavorite(image, evt)">{{ getFavoriteIconName(image) }}
        </v-icon>
        <v-icon class="closeIcon" @click="closeDialog">close</v-icon>
        <v-progress-circular v-if="! largeLoaded" class="largePreviewLoadingProgress" :size="70" indeterminate/>
        <v-img :src="largeImageUrl" :lazy-src="smallImageUrl" contain alt="" ref="large" class="largeImage"
               :max-height="largeImageMaxHeight"
               @load="onLargeLoaded">
        </v-img>
        <span v-if="largeLoaded" class="label">{{ displayId }}</span>
        <v-icon v-if="hasPreviousPreview" class="previous" size="60px" @click="openPrevious">navigate_before</v-icon>
        <v-icon v-if="hasNextPreview" class="next" @click="openNext" size="60px">navigate_next</v-icon>
      </v-dialog>
    </div>
  </div>
</template>

<script lang="ts">
import Component from 'vue-class-component'
import {Prop} from "vue-property-decorator";
import BasicComponent from "@/global/BasicComponent";
import {ImageApi, ImageDto, ImageOrientationDto} from "@swagger";

@Component({
  components: {}
})
export default class ImagePreview extends BasicComponent {

  @Prop(Object) readonly image!: ImageDto;
  @Prop(String) readonly filter: string;
  @Prop(Boolean) readonly doNotOpenOnClick!: boolean;
  @Prop(String) readonly smallHeight: string;
  @Prop(String) readonly smallWidth: string;
  @Prop(Boolean) readonly hasPreviousPreview: boolean; // When using as a slider
  @Prop(Boolean) readonly hasNextPreview: boolean;
  /**
   * The component allows to toggle the favorite state
   */
  @Prop({
    type: Boolean,
    default: true
  }) readonly favoriteEditable: boolean;
  @Prop({
    type: Boolean,
    default: true
  }) readonly showFavoriteIcon: boolean;

  dialog: boolean = false;
  smallLoaded: boolean = false;
  largeLoaded: boolean = false;
  mayLoadLargeImage: boolean = false;

  created() {
    const _this = this;
    document.addEventListener('keyup', function (evt) {
      let cancel = false;
      if (evt.keyCode === 37 && _this.dialog) { // Arrow left
        _this.openPrevious();
        cancel = true;
      } else if (evt.keyCode === 39 && _this.dialog) { // Arrow right
        _this.openNext();
        cancel = true;
      }
      if (cancel) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
      }
    });
  }

  mounted() {
  }

  onImageClick() {
    this.$emit("select_image");
    if (this.doNotOpenOnClick) return;
    this.openDialog();
  }

  getFavoriteIconName(image: ImageDto): string {
    return image.favorite ? "favorite" : "favorite_border";
  }

  openDialog() {
    this.dialog = true;
    this.trackEvent("Bild", "Vergrößerte Ansicht", undefined, this.image?.uniqueId);
  }

  closeDialog() {
    this.dialog = false;
  }

  openPrevious() {
    if (!this.dialog) return;
    this.closeDialog();
    this.$emit("openPrevious");
  }

  openNext() {
    if (!this.dialog) return;
    this.closeDialog();
    this.$emit("openNext");
  }

  largeImageWidth() {
    if (!this.$refs.large) return this.getSmallWidth;
    return this.$refs.large["image"]["naturalWidth"];
  }

  onSmallLoaded() {
    this.smallLoaded = true;
    this.$emit("small-loaded");
    const data = this;
    this.$nextTick(() => {
      this.$refs.small["ondragstart"] = () => false;
    });
    setTimeout(() => {
      data.mayLoadLargeImage = true;
    }, 5 * 1000);
  }

  onLargeLoaded() {
    this.largeLoaded = true;
    this.$emit("large-loaded");
    this.$nextTick(() => {
      this.$refs.large["ondragstart"] = () => false;
    });
  }

  toggleFavorite(image: ImageDto, evt: MouseEvent) {
    if (!this.favoriteEditable) {
      this.onImageClick();
      return;
    }
    if (image.favorite) {
      this.$set(image, "favorite", false);
      new ImageApi(this.apiConfiguration).removeFavoriteImage(image.uniqueId)
          .then(() => {
            this.trackEvent("Bild", "Defavorisieren", undefined, image.uniqueId);
          })
          .catch((err) => {
            console.error(err);
          });
    } else {
      this.$set(image, "favorite", true);
      new ImageApi(this.apiConfiguration).addFavoriteImage(image.uniqueId)
          .then(() => {
            this.trackEvent("Bild", "Favorisieren", undefined, image.uniqueId);
          })
          .catch((err) => {
            console.error(err);
          });
    }
  }

  get smallImageUrl() {
    return this.getImageUrl(this.image?.smallUrl, this.filter);
  }

  get largeImageUrl() {
    return this.getImageUrl(this.image?.largeUrl, this.filter);
  }

  get displayId() {
    return this.image?.label;
  }

  get landscape() {
    return this.image?.orientation === ImageOrientationDto.LANDSCAPE;
  }

  get getSmallHeight() {
    if (this.smallHeight) return this.smallHeight;
    return this.landscape ? "92px" : "128px";
  }

  get getSmallWidth() {
    if (this.smallWidth) return this.smallWidth;
    return this.landscape ? "128px" : "92px";
  }

  get largeImageMaxHeight() {
    if (this.largeLoaded) {
      return "90vh";
    }
    return this.getSmallHeight;
  }

  get displayFavoriteIcon(): boolean {
    return this.showFavoriteIcon && (this.favoriteEditable || (this.image?.favorite || false))
  }

}

</script>
<style lang="scss" scoped>

.imageWrapper {
  line-height: 0;
  margin: 0;

  .smallPreview {
    position: relative;
    display: block;
    left: 50%;
    transform: translateX(-50%);

    .smallImage {
      margin: 0 auto;

      &:hover {
        cursor: pointer;
      }
    }

    .label {
      $size: 30px;
      position: absolute;
      bottom: 4px;
      right: 4px;
      color: white;
      font-size: 16px;
      font-weight: bold;
      text-align: center;
      padding-top: calc(#{$size} / 2);
      padding-left: 2px;
      height: $size;
      width: $size;
      border-radius: $size;
      background-color: rgba(0, 0, 0, 0.5);
    }
  }
}

</style>
<style lang="scss">

.favoriteIcon {
  position: absolute !important;
  color: rgba(255, 255, 255, 0.65) !important;

  &.editable {
    &.active, &:hover {
      color: white !important;
    }
  }

  &.small {
    z-index: 5;
    top: 6px;
    left: 6px;
  }

  &.large {
    z-index: 60;
    top: 15px;
    left: 15px;
    font-size: 40px;

    &:not(.editable) {
      &:hover {
        cursor: default;
      }
    }
  }
}

.largePreviewDialog {
  line-height: 0;
  position: relative;
  overflow: hidden;

  & > * {
    width: auto;
  }

  .closeIcon {
    cursor: pointer;

    position: absolute;
    right: 10px;
    top: 10px;
    z-index: 200;
  }

  $dark: rgba(0, 0, 0, 0.5);

  .label {
    $size: 50px;
    position: absolute;
    bottom: 8px;
    right: 8px;
    color: white;
    font-size: 24px;
    font-weight: bold;
    text-align: center;
    padding-top: calc(#{$size} / 2);
    padding-left: 2px;
    height: $size;
    width: $size;
    border-radius: $size;
    background-color: $dark;
  }

  .previous, .next {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: $dark;

    &:hover {
      cursor: pointer;
      color: black;
    }
  }

  .previous {
    left: 10px;
  }

  .next {
    right: 10px;
  }
}

.largeImage {
  max-height: 90vh;
}

.largePreviewLoadingProgress {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2000;
}

</style>
